import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { Euro, Send, CheckCircle, Clock, XCircle, AlertCircle, Loader2, CreditCard } from 'lucide-react';
import { motion } from 'framer-motion';

const STATUS_MAP = {
  pending: { label: 'En attente', color: 'text-[#FFB800] bg-[#FFB800]/10 border-[#FFB800]/30', icon: Clock },
  approved: { label: 'Accepté', color: 'text-[#00FFFF] bg-[#00FFFF]/10 border-[#00FFFF]/30', icon: CheckCircle },
  paid: { label: 'Payé', color: 'text-[#00FF88] bg-[#00FF88]/10 border-[#00FF88]/30', icon: CheckCircle },
  rejected: { label: 'Refusé', color: 'text-[#FF3B5C] bg-[#FF3B5C]/10 border-[#FF3B5C]/30', icon: XCircle },
};

export default function InfluencerPayouts() {
  const [balance, setBalance] = useState(0);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [amount, setAmount] = useState('');
  const [iban, setIban] = useState('');
  const [holderName, setHolderName] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchData = async () => {
    try {
      const [dashRes, payoutsRes] = await Promise.all([
        api.get('/influencer/dashboard'),
        api.get('/influencer/payouts')
      ]);
      setBalance(dashRes.data.stats?.balance_available || 0);
      setPayouts(payoutsRes.data.payouts || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitLoading(true);
    try {
      await api.post('/influencer/payouts/request', {
        amount: parseFloat(amount),
        iban: iban.replace(/\s/g, ''),
        holder_name: holderName
      });
      setSuccess('Demande de virement soumise');
      setShowForm(false);
      setAmount('');
      setIban('');
      setHolderName('');
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la demande');
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8 space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-48 bg-[#2A2A2D] rounded" />
          <div className="h-32 bg-[#2A2A2D] rounded-lg" />
          <div className="h-48 bg-[#2A2A2D] rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="influencer-payouts-page">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Euro className="h-6 w-6 text-[#FF6B35]" />Paiements</h1>
        <p className="text-[#6B6B6E] text-sm mt-1">Gérez vos demandes de virement</p>
      </div>

      {/* Balance Card */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-[#1A1A1D] to-[#222226] p-6 rounded-lg border border-[#FF6B35]/20" data-testid="balance-card">
        <p className="text-[#6B6B6E] text-xs font-semibold uppercase mb-2">Solde disponible</p>
        <div className="text-4xl font-bold text-[#00FF88] mb-4">{balance.toFixed(2)}€</div>
        {!showForm ? (
          <button onClick={() => setShowForm(true)} disabled={balance < 10}
            className="btn-primary py-2.5 px-6 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="request-payout-btn">
            <Send className="h-4 w-4" />Demander un virement
          </button>
        ) : null}
        {balance < 10 && !showForm && <p className="text-[#6B6B6E] text-xs mt-2">Minimum : 10€</p>}
      </motion.div>

      {/* Payout Form */}
      {showForm && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="bg-[#1A1A1D] p-6 rounded-lg border border-[#FF6B35]/30" data-testid="payout-form">
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><CreditCard className="h-5 w-5 text-[#FF6B35]" />Demande de virement</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-[#B8B8BA] text-xs font-semibold uppercase mb-1 block">Montant (€)</label>
              <input type="number" value={amount} onChange={e => setAmount(e.target.value)} min="10" max={balance} step="0.01" required
                className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-2.5 text-white text-sm focus:border-[#FF6B35] focus:outline-none" placeholder={`Max : ${balance.toFixed(2)}€`} data-testid="payout-amount" />
            </div>
            <div>
              <label className="text-[#B8B8BA] text-xs font-semibold uppercase mb-1 block">IBAN</label>
              <input type="text" value={iban} onChange={e => setIban(e.target.value.toUpperCase())} required minLength={15} maxLength={34}
                className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-2.5 text-white text-sm font-mono focus:border-[#FF6B35] focus:outline-none" placeholder="FR76 3000 1007 9412 3456 7890 185" data-testid="payout-iban" />
            </div>
            <div>
              <label className="text-[#B8B8BA] text-xs font-semibold uppercase mb-1 block">Titulaire du compte</label>
              <input type="text" value={holderName} onChange={e => setHolderName(e.target.value)} required minLength={2}
                className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-2.5 text-white text-sm focus:border-[#FF6B35] focus:outline-none" placeholder="Nom Prénom" data-testid="payout-holder" />
            </div>
            {error && (
              <div className="bg-[#FF3B5C]/10 border border-[#FF3B5C]/30 p-3 rounded-lg flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-[#FF3B5C] shrink-0" /><span className="text-[#FF3B5C] text-sm">{error}</span>
              </div>
            )}
            <div className="flex gap-3">
              <button type="submit" disabled={submitLoading} className="btn-primary py-2.5 px-6 flex items-center gap-2 disabled:opacity-50" data-testid="payout-submit">
                {submitLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}Soumettre
              </button>
              <button type="button" onClick={() => { setShowForm(false); setError(''); }} className="btn-secondary py-2.5 px-6">Annuler</button>
            </div>
          </form>
        </motion.div>
      )}

      {success && (
        <div className="bg-[#00FF88]/10 border border-[#00FF88]/30 p-3 rounded-lg flex items-center gap-2">
          <CheckCircle className="h-4 w-4 text-[#00FF88]" /><span className="text-[#00FF88] text-sm">{success}</span>
        </div>
      )}

      {/* Payouts History */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="bg-[#1A1A1D] p-6 rounded-lg border border-[#3A3A3D]/40" data-testid="payouts-history">
        <h2 className="text-lg font-bold text-white mb-4">Historique des demandes</h2>
        {payouts.length === 0 ? (
          <p className="text-[#6B6B6E] text-sm">Aucune demande de virement</p>
        ) : (
          <div className="space-y-3">
            {payouts.map((p, i) => {
              const s = STATUS_MAP[p.status] || STATUS_MAP.pending;
              const Icon = s.icon;
              return (
                <div key={i} className="bg-[#222226] p-4 rounded-lg border border-[#3A3A3D]/30 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`payout-${i}`}>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-white font-bold">{p.amount?.toFixed(2)}€</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${s.color} flex items-center gap-1`}>
                        <Icon className="h-3 w-3" />{s.label}
                      </span>
                    </div>
                    <p className="text-[#6B6B6E] text-xs">IBAN : {p.iban?.replace(/(.{4})/g, '$1 ').trim()}</p>
                    <p className="text-[#6B6B6E] text-xs">Titulaire : {p.holder_name}</p>
                  </div>
                  <div className="text-right text-xs text-[#6B6B6E]">
                    <p>Demandé le {new Date(p.created_at).toLocaleDateString('fr-FR')}</p>
                    {p.processed_at && <p>Traité le {new Date(p.processed_at).toLocaleDateString('fr-FR')}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </motion.div>
    </div>
  );
}
